
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Col, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import Button from '@mui/joy/Button';
import JobCardGrid from './JobCardGrid';
import CreateJobButton from './CreateJobButton';
import { CircleX, Trash2 } from 'lucide-react';
import { RootState } from '../../index';
import { fetchJobs } from '../../slices/jobs/thunk';
import { removeJob } from '../../slices/jobs/reducer';
import { useUser } from '../../hooks';

const ActiveJobs = () => {
  const dispatch = useDispatch();
  const { currentUserId } = useUser();
  const { jobs, loading } = useSelector((state: RootState) => state.Jobs);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  useEffect(() => {
    console.log('🔍 ActiveJobs Debug:', {
      currentUserId,
      hasUserId: !!currentUserId,
      jobsLength: jobs.length,
      loading,
      hasAddJobCard: jobs.some(job => job.id === 'add-job-card')
    });
  }, [currentUserId, jobs, loading]);
  
  useEffect(() => {
    if (currentUserId) {
      console.log('📞 Calling fetchJobs for user:', currentUserId);
      dispatch(fetchJobs(currentUserId) as any);
    } else {
      console.log('❌ No currentUserId, not fetching jobs');
    }
  }, [dispatch, currentUserId]);

  const handleDeleteJob = async (jobId: string) => {
    setIsDeleting(true);
    try {
      // Perform the delete operation in Firebase
      const firestore = firebase.firestore();
      await firestore.collection('jobs').doc(jobId).delete();

      // Update Redux store
      dispatch(removeJob(jobId));

      setDeleteAlert({ type: 'success', message: 'Job deleted successfully' });
      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting job:', error);
      setDeleteAlert({ type: 'error', message: 'Error deleting job. Please try again.' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = (jobId: string) => {
    setJobToDelete(jobId);
    setDeleteModalOpen(true);
  };

  return (
    <React.Fragment>
      <Col lg={12}>
        <Card>
          <CardHeader className="d-flex align-items-center">
            <h4 className="card-title flex-grow-1 mb-0">Jobs</h4>
            <div className="d-flex gap-1">
              <CreateJobButton
                variant="primary"
                className="me-2"
                onJobCreated={currentUserId ? () => dispatch(fetchJobs(currentUserId) as any) : undefined}
              />
            </div>
          </CardHeader>
          <CardBody>
            {deleteAlert && (
              <Alert
                color={deleteAlert.type}
                toggle={() => setDeleteAlert(null)}
                dismissible
                className="mb-3"
              >
                {deleteAlert.message}
              </Alert>
            )}

            <JobCardGrid
              jobs={jobs}
              onDeleteJob={handleDeleteClick}
              isLoading={loading}
            />

            <div className="mt-4 text-muted">
              Showing {jobs.length} jobs
            </div>
          </CardBody>
        </Card>
      </Col>

      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>Confirm Delete</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this job? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button
            variant="soft"
            color="neutral"
            className="me-2"
            onClick={() => setDeleteModalOpen(false)}
          >
            <CircleX className="me-2" />Cancel
          </Button>
          <Button
            variant="solid"
            color="danger"
            loading={isDeleting}
            onClick={() => jobToDelete && handleDeleteJob(jobToDelete)}
          >
            <Trash2 className="me-2" />Delete
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ActiveJobs;